import { FC } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import { ClientContact } from '../utils/api';

// Suggestions only — free-text so clinics aren't boxed in (same pattern as
// the barn contacts editor). Covers the clinic-feedback cases: spouse/family
// pairs and the "contact for the appointment who doesn't pay the bills".
const ROLE_SUGGESTIONS = [
  'Spouse / Partner',
  'Family Member',
  'Appointment Contact',
  'Barn Manager',
  'Trainer',
  'Emergency Contact',
];

const blankContact: ClientContact = { name: '', role: '', phone: '', email: '' };

interface ClientContactsEditorProps {
  contacts: ClientContact[];
  onChange: (contacts: ClientContact[]) => void;
}

/**
 * Row-based editor for a client's additional contacts. Controlled: the parent
 * owns the array (part of the client form state) so the rows ride the same
 * create/update mutation — and therefore the same offline write queue — as
 * the rest of the client form.
 */
const ClientContactsEditor: FC<ClientContactsEditorProps> = ({ contacts, onChange }) => {
  const setContact = (index: number, field: keyof ClientContact, value: string) => {
    const next = [...contacts];
    next[index] = { ...next[index], [field]: value };
    onChange(next);
  };

  const addContact = () => onChange([...contacts, { ...blankContact }]);

  const removeContact = (index: number) => onChange(contacts.filter((_, i) => i !== index));

  return (
    <Box>
      <Divider sx={{ mb: 1 }} />
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Additional contacts</Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={addContact}>
          Add contact
        </Button>
      </Box>
      {contacts.length === 0 && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
          Add a spouse, family member, or the person to call about appointments.
        </Typography>
      )}

      {contacts.map((contact, index) => (
        <Box
          key={index}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
            mt: 1.5,
          }}
        >
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={11} sm={6}>
              <TextField
                label="Name"
                size="small"
                value={contact.name}
                onChange={(e) => setContact(index, 'name', e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 120 }}
              />
            </Grid>
            <Grid item xs={12} sm={5} order={{ xs: 3, sm: 2 }}>
              <Autocomplete
                freeSolo
                size="small"
                options={ROLE_SUGGESTIONS}
                value={contact.role ?? ''}
                onChange={(_e, value) => setContact(index, 'role', value ?? '')}
                onInputChange={(_e, value) => setContact(index, 'role', value)}
                renderInput={(params) => (
                  <TextField {...params} label="Role" placeholder="Spouse / Partner" />
                )}
              />
            </Grid>
            <Grid item xs={1} sm={1} order={{ xs: 2, sm: 3 }} sx={{ textAlign: 'right' }}>
              <IconButton
                size="small"
                aria-label="Remove contact"
                onClick={() => removeContact(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Grid>
            <Grid item xs={12} sm={6} order={4}>
              <TextField
                label="Phone"
                size="small"
                value={contact.phone}
                onChange={(e) => setContact(index, 'phone', e.target.value)}
                fullWidth
                inputProps={{ maxLength: 40 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} order={5}>
              <TextField
                label="Email"
                type="email"
                size="small"
                value={contact.email}
                onChange={(e) => setContact(index, 'email', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>
      ))}
    </Box>
  );
};

export default ClientContactsEditor;
